import * as React from 'react';
import { loadPTTBinding } from '@/stores/use-ptt-binding-store.ts';

export function usePushToTalk(
  toggleMic: (enabled?: boolean) => Promise<void> | void,
  enabled: boolean
) {
  const pressedRef = React.useRef(false);
  const bindingRef = React.useRef(loadPTTBinding());

  React.useEffect(() => {
    function isTypingTarget(e: Event) {
      const t = e.target as HTMLElement | null;
      return (
        t?.tagName === 'INPUT' ||
        t?.tagName === 'TEXTAREA' ||
        t?.isContentEditable
      );
    }

    function down() {
      if (pressedRef.current) return;
      pressedRef.current = true;
      if (!enabled) toggleMic(true);
    }

    function up() {
      if (!pressedRef.current) return;
      pressedRef.current = false;
      if (enabled) toggleMic(false);
    }

    function onKeyDown(e: KeyboardEvent) {
      const b = bindingRef.current;
      if (b.type !== 'keyboard') return;
      if (e.code !== b.code) return;
      if (e.repeat || isTypingTarget(e)) return;
      down();
    }

    function onKeyUp(e: KeyboardEvent) {
      const b = bindingRef.current;
      if (b.type !== 'keyboard') return;
      if (e.code !== b.code) return;
      up();
    }

    function onMouseDown(e: MouseEvent) {
      const b = bindingRef.current;
      if (b.type !== 'mouse') return;
      if (e.button !== b.button) return;
      e.preventDefault();
      down();
    }

    function onMouseUp(e: MouseEvent) {
      const b = bindingRef.current;
      if (b.type !== 'mouse') return;
      if (e.button !== b.button) return;
      e.preventDefault();
      up();
    }

    function onStorage(e: StorageEvent) {
      if (e.key === 'ptt-binding' && e.newValue) {
        bindingRef.current = JSON.parse(e.newValue);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('storage', onStorage);
    };
  }, [toggleMic, enabled]);
}
